'use client';

import { FILTER_CONTROL_STYLE } from './ui/FilterPill';
import { Box, HStack, Text, IconButton, Portal, createListCollection } from '@chakra-ui/react';
import { format } from 'date-fns';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import {
  AnalyticsDateRangePicker,
  type AnalyticsDateRangeValue,
} from '@/components/shared/AnalyticsDateRangePicker';
import { useVoicePageControl, voiceSelectOption } from '@/lib/voice/page-controls';
import { Select } from '@chakra-ui/react';
import { Tooltip } from '@chakra-ui/react';
import {
  groupJobsByMonth,
  groupJobsByPeriod,
  groupJobsByWeek,
  type MonthJobBucket,
  type PeriodGranularity,
  type PeriodJobBucket,
  type WeekJobBucket,
  type WeekJobRecord,
} from '@/utils/weekJobBuckets';

/** Range pills / tooltips use `date-fns` `format()` in the browser (local wall time), matching `groupJobsByWeek` in `@/utils/weekJobBuckets` — see that module’s timezone policy. */

const PILL_COLOR_TRANSITION =
  'background-color 150ms ease, border-color 150ms ease, color 150ms ease';
const SELECTED_PILL_PROPS = {
  color: 'ui.text.primary',
  bg: 'ui.surface.input',
  borderColor: 'ui.border.default',
  _hover: {
    bg: 'ui.surface.selected',
    borderColor: 'ui.border.default',
    color: 'ui.text.primary',
  },
} as const;
const UNSELECTED_PILL_PROPS = {
  color: 'ui.text.secondary',
  bg: 'transparent',
  borderColor: 'ui.border.subtle',
  _hover: {
    bg: 'ui.surface.input',
    borderColor: 'ui.border.default',
    color: 'ui.text.primary',
  },
} as const;
const PERIOD_SELECT_CONTENT_PROPS = {
  bg: 'ui.surface.card',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'ui.border.subtle',
  borderRadius: 'glassButton',
  boxShadow: 'none',
  backdropFilter: 'blur(22px)',
  css: { WebkitBackdropFilter: 'blur(22px)' },
  p: '1',
} as const;
const PERIOD_SELECT_ITEM_PROPS = {
  minH: '38px',
  px: '2.5',
  py: '1.5',
  borderRadius: 'glassInput',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  color: 'ui.text.secondary',
  fontSize: 'xs',
  _hover: { bg: 'ui.surface.input', color: 'ui.text.primary' },
  _highlighted: { bg: 'ui.surface.input', color: 'ui.text.primary' },
  _focus: { boxShadow: 'none', outline: 'none' },
} as const;
function getSelectablePillProps(isSelected: boolean) {
  return isSelected ? SELECTED_PILL_PROPS : UNSELECTED_PILL_PROPS;
}

export interface Job extends WeekJobRecord {
  brand_name: string | null;
}

/** Selected calendar week: stable key plus all sampler job ids in that week (newest first). */
export interface WeekSelectorWeekSelection {
  grain?: 'week';
  weekKey: string;
  monthKey?: string;
  jobIds: string[];
}

/** Selected calendar month: stable key plus all sampler job ids in that month (newest first). */
export interface WeekSelectorMonthSelection {
  grain: 'month';
  monthKey: string;
  weekKey?: string;
  jobIds: string[];
}

export interface WeekSelectorRangeSelection {
  grain: 'range';
  rangeKey: WeekSelectorRangeKey;
  label: string;
  fullLabel: string;
  startDate: string;
  endDate: string;
  jobIds: string[];
}

export type WeekSelectorSelection =
  | WeekSelectorWeekSelection
  | WeekSelectorMonthSelection
  | WeekSelectorRangeSelection;
export type WeekSelectorValue = 'all' | WeekSelectorSelection;
export type WeekSelectorPresetRangeKey =
  | 'last_7_days'
  | 'last_30_days'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_year';
export type WeekSelectorRangeKey = WeekSelectorPresetRangeKey | 'custom';

/** Params for pipeline queries: omit both for “all jobs in project”. */
export type PipelineJobScopeParams =
  | Record<string, never>
  | { job_id: string }
  | { job_ids: string[] }
  | { start_date: string; end_date: string };

/** Params for server-side period filtering anchored to sampler_jobs.created_at. */
export type PipelinePeriodScopeParams = {
  period_grain: PeriodGranularity;
  period_key: string;
};

export function jobScopeParamsFromWeekSelection(v: WeekSelectorValue): PipelineJobScopeParams {
  if (v === 'all') return {};
  if (v.grain === 'range') {
    return { start_date: v.startDate, end_date: v.endDate };
  }
  if (v.jobIds.length === 1) return { job_id: v.jobIds[0] };
  return { job_ids: v.jobIds };
}

function getSelectionPeriodKey(selection: WeekSelectorSelection): string {
  if (selection.grain === 'range') {
    return selection.rangeKey;
  }
  if (selection.grain === 'month') {
    return selection.monthKey;
  }
  return selection.weekKey;
}

function getBucketPeriodKey(bucket: PeriodJobBucket): string {
  return bucket.grain === 'month' ? bucket.monthKey : bucket.weekKey;
}

function selectionFromBucket(bucket: PeriodJobBucket): WeekSelectorSelection {
  if (bucket.grain === 'month') {
    return {
      grain: 'month',
      monthKey: bucket.monthKey,
      jobIds: bucket.jobIds,
    };
  }
  return {
    grain: 'week',
    weekKey: bucket.weekKey,
    jobIds: bucket.jobIds,
  };
}

function getBucketLabel(bucket: PeriodJobBucket): string {
  return bucket.grain === 'month'
    ? bucket.label
    : formatCalendarWeekRangeLabel(bucket.weekStart, bucket.weekEnd);
}

export function periodScopeParamsFromWeekSelection(
  v: WeekSelectorValue
): PipelinePeriodScopeParams | null {
  if (v === 'all') return null;
  if (v.grain === 'range') return null;
  if (v.grain === 'month') {
    return {
      period_grain: 'month',
      period_key: v.monthKey,
    };
  }
  return {
    period_grain: 'week',
    period_key: v.weekKey,
  };
}

/** Newest calendar week for the given jobs, or null if empty. */
export function firstWeekSelectionFromJobs(
  jobs: readonly WeekJobRecord[]
): WeekSelectorWeekSelection | null {
  const b = groupJobsByWeek(jobs)[0];
  if (!b) return null;
  return { weekKey: b.weekKey, jobIds: b.jobIds, grain: 'week' };
}

export function firstPeriodSelectionFromJobs(
  jobs: readonly WeekJobRecord[],
  granularity: PeriodGranularity
): WeekSelectorSelection | null {
  const bucket = groupJobsByPeriod(jobs, granularity)[0];
  if (!bucket) return null;
  if (bucket.grain === 'month') {
    return {
      grain: 'month',
      monthKey: bucket.monthKey,
      jobIds: bucket.jobIds,
    };
  }
  return {
    grain: 'week',
    weekKey: bucket.weekKey,
    jobIds: bucket.jobIds,
  };
}

const RANGE_LABELS: Record<WeekSelectorPresetRangeKey, { current: string; previous: string }> = {
  last_7_days: { current: 'Last 7 days', previous: 'Previous 7 days' },
  last_30_days: { current: 'Last 30 days', previous: 'Previous 30 days' },
  last_3_months: { current: 'Last 3 months', previous: 'Previous 3 months' },
  last_6_months: { current: 'Last 6 months', previous: 'Previous 6 months' },
  last_year: { current: 'Last year', previous: 'Previous year' },
};

function shiftDateByMonths(date: Date, monthDelta: number): Date {
  const shifted = new Date(date.getTime());
  const originalDay = shifted.getDate();
  shifted.setDate(1);
  shifted.setMonth(shifted.getMonth() + monthDelta);
  const lastDayOfTargetMonth = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate();
  shifted.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  return shifted;
}

function shiftDateByRange(date: Date, rangeKey: WeekSelectorPresetRangeKey): Date {
  const shifted = new Date(date.getTime());
  if (rangeKey === 'last_7_days') {
    shifted.setDate(shifted.getDate() - 6);
  } else if (rangeKey === 'last_30_days') {
    shifted.setDate(shifted.getDate() - 29);
  } else if (rangeKey === 'last_3_months') {
    return shiftDateByMonths(date, -3);
  } else if (rangeKey === 'last_6_months') {
    return shiftDateByMonths(date, -6);
  } else if (rangeKey === 'last_year') {
    return shiftDateByMonths(date, -12);
  }
  return shifted;
}

function rangeSelectionFromWindow(
  rangeKey: WeekSelectorPresetRangeKey,
  end: Date,
  label: string
): WeekSelectorRangeSelection {
  const normalizedEnd = new Date(end.getTime());
  normalizedEnd.setHours(23, 59, 59, 999);
  const start = shiftDateByRange(normalizedEnd, rangeKey);
  start.setHours(0, 0, 0, 0);

  return {
    grain: 'range',
    rangeKey,
    label,
    fullLabel: `${label}: ${format(start, 'MMM d')} - ${format(normalizedEnd, 'MMM d, yyyy')}`,
    startDate: start.toISOString(),
    endDate: normalizedEnd.toISOString(),
    jobIds: [],
  };
}

export function rangeSelectionFromJobs(
  _jobs: readonly WeekJobRecord[],
  rangeKey: WeekSelectorPresetRangeKey,
  end: Date = new Date()
): WeekSelectorRangeSelection {
  return rangeSelectionFromWindow(rangeKey, end, RANGE_LABELS[rangeKey].current);
}

export function previousRangeSelectionFromJobs(
  _jobs: readonly WeekJobRecord[],
  currentSelection: WeekSelectorRangeSelection
): WeekSelectorRangeSelection {
  const currentStart = new Date(currentSelection.startDate).getTime();
  const currentEnd = new Date(currentSelection.endDate).getTime();
  const duration = Math.max(0, currentEnd - currentStart);
  const previousEnd = new Date(currentStart - 1);
  const previousStart = new Date(previousEnd.getTime() - duration);
  const label =
    currentSelection.rangeKey === 'custom'
      ? 'Previous period'
      : RANGE_LABELS[currentSelection.rangeKey].previous;
  return {
    grain: 'range',
    rangeKey: currentSelection.rangeKey,
    label,
    fullLabel: `${label}: ${format(previousStart, 'MMM d')} - ${format(previousEnd, 'MMM d, yyyy')}`,
    startDate: previousStart.toISOString(),
    endDate: previousEnd.toISOString(),
    jobIds: [],
  };
}

export function customRangeSelection(
  startDate: string,
  endDate: string
): WeekSelectorRangeSelection {
  const [startYear, startMonth, startDay] = startDate.slice(0, 10).split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.slice(0, 10).split('-').map(Number);
  const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
  const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
  return {
    grain: 'range',
    rangeKey: 'custom',
    label: 'Custom',
    fullLabel: `Custom: ${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    jobIds: [],
  };
}

export function localCalendarDateKey(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? format(date, 'yyyy-MM-dd') : value.slice(0, 10);
}

export function dateRangeSelectionsFromJobs(
  jobs: readonly WeekJobRecord[],
  end: Date = new Date()
): WeekSelectorRangeSelection[] {
  return (
    ['last_7_days', 'last_30_days', 'last_3_months', 'last_6_months', 'last_year'] as const
  ).map(rangeKey => rangeSelectionFromJobs(jobs, rangeKey, end));
}

export function last7DaysSelectionFromJobs(
  jobs: readonly WeekJobRecord[],
  end: Date = new Date()
): WeekSelectorRangeSelection {
  return rangeSelectionFromJobs(jobs, 'last_7_days', end);
}

export function previous7DaysSelectionFromJobs(
  jobs: readonly WeekJobRecord[],
  end: Date = new Date()
): WeekSelectorRangeSelection {
  return previousRangeSelectionFromJobs(jobs, last7DaysSelectionFromJobs(jobs, end));
}

export function useRollingRangeAnchor(): Date {
  const endOfLocalDay = useCallback((date: Date) => {
    const end = new Date(date.getTime());
    end.setHours(23, 59, 59, 999);
    return end;
  }, []);
  const [anchor, setAnchor] = useState(() => endOfLocalDay(new Date()));

  useEffect(() => {
    let timeoutId: number | undefined;
    const scheduleNextDay = () => {
      const now = new Date();
      const nextDay = new Date(now.getTime());
      nextDay.setHours(24, 0, 1, 0);
      timeoutId = window.setTimeout(
        () => {
          setAnchor(endOfLocalDay(new Date()));
          scheduleNextDay();
        },
        Math.max(1_000, nextDay.getTime() - now.getTime())
      );
    };

    scheduleNextDay();
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [endOfLocalDay]);

  return anchor;
}

export function isRollingWeekSelection(
  value: WeekSelectorValue,
  rangeKey?: WeekSelectorPresetRangeKey
): value is WeekSelectorRangeSelection & { rangeKey: WeekSelectorPresetRangeKey } {
  return (
    value !== 'all' &&
    value.grain === 'range' &&
    value.rangeKey !== 'custom' &&
    (!rangeKey || value.rangeKey === rangeKey)
  );
}

/** Resolve URL/deep-link job id to the week bucket that contains it. */
export function weekSelectionContainingJobId(
  jobs: readonly WeekJobRecord[],
  jobId: string
): WeekSelectorValue {
  if (!jobId) return 'all';
  for (const b of groupJobsByWeek(jobs)) {
    if (b.jobIds.includes(jobId)) {
      return { weekKey: b.weekKey, jobIds: b.jobIds };
    }
  }
  return 'all';
}

export function periodSelectionContainingJobId(
  jobs: readonly WeekJobRecord[],
  jobId: string,
  granularity: PeriodGranularity
): WeekSelectorValue {
  if (!jobId) return 'all';
  if (granularity === 'month') {
    for (const b of groupJobsByMonth(jobs)) {
      if (b.jobIds.includes(jobId)) {
        return { grain: 'month', monthKey: b.monthKey, jobIds: b.jobIds };
      }
    }
    return 'all';
  }
  return weekSelectionContainingJobId(jobs, jobId);
}

/** Compact range for period labels (matches week pill wording). */
export function formatCalendarWeekRangeLabel(weekStart: Date, weekEnd: Date): string {
  const y1 = weekStart.getFullYear();
  const y2 = weekEnd.getFullYear();
  const m1 = weekStart.getMonth();
  const m2 = weekEnd.getMonth();
  if (y1 === y2 && m1 === m2) {
    return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'd, yyyy')}`;
  }
  if (y1 === y2) {
    return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
  }
  return `${format(weekStart, 'MMM d, yyyy')} – ${format(weekEnd, 'MMM d, yyyy')}`;
}

function weekSelectionsEqual(a: WeekSelectorValue, b: WeekSelectorValue): boolean {
  if (a === 'all' || b === 'all') return a === b;
  if (a.grain === 'range' || b.grain === 'range') {
    if (a.grain !== 'range' || b.grain !== 'range' || a.rangeKey !== b.rangeKey) return false;
    if (a.rangeKey !== 'custom') return true;
    return a.startDate === b.startDate && a.endDate === b.endDate;
  }
  return (
    getSelectionPeriodKey(a) === getSelectionPeriodKey(b) &&
    (a.grain || 'week') === (b.grain || 'week')
  );
}

interface WeekSelectorProps {
  jobs: Job[];
  /** When provided, skips internal grouping (caller pre-buckets). */
  weekBuckets?: WeekJobBucket[];
  /** Optional month buckets for month granularity. */
  monthBuckets?: MonthJobBucket[];
  rangeOptions?: WeekSelectorRangeSelection[];
  rangeOnly?: boolean;
  /** Legacy deep-link grain only. The date picker is intentionally the sole visible time control. */
  granularity?: PeriodGranularity;
  allowMonth?: boolean;
  value: WeekSelectorValue;
  onChange: (value: WeekSelectorValue) => void;
  showAll?: boolean;
  allLabel?: string;
  loading?: boolean;
  variant?: 'surface' | 'toolbar';
  showComparison?: boolean;
}

export function WeekSelector({
  jobs,
  weekBuckets: weekBucketsProp,
  monthBuckets: monthBucketsProp,
  rangeOptions = [],
  rangeOnly = false,
  granularity = 'week',
  allowMonth = false,
  value,
  onChange,
  showAll = true,
  allLabel = 'All',
  loading = false,
  variant = 'surface',
  showComparison = true,
}: WeekSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const effectiveGranularity: PeriodGranularity = allowMonth ? granularity : 'week';

  const weekBuckets = useMemo((): WeekJobBucket[] => {
    if (weekBucketsProp?.length) return weekBucketsProp;
    return groupJobsByWeek(jobs);
  }, [jobs, weekBucketsProp]);

  const monthBuckets = useMemo((): MonthJobBucket[] => {
    if (monthBucketsProp?.length) return monthBucketsProp;
    return groupJobsByMonth(jobs);
  }, [jobs, monthBucketsProp]);

  const buckets = useMemo((): PeriodJobBucket[] => {
    if (effectiveGranularity === 'month') {
      return monthBuckets;
    }
    return weekBuckets;
  }, [effectiveGranularity, monthBuckets, weekBuckets]);
  const selectableBuckets = useMemo((): PeriodJobBucket[] => {
    if (!rangeOnly) return buckets;
    if (value === 'all' || value.grain === 'range') return [];

    const activeBuckets = value.grain === 'month' ? monthBuckets : weekBuckets;
    return activeBuckets.filter(bucket => {
      if (value.grain === 'month' && bucket.grain === 'month') {
        return bucket.monthKey === value.monthKey;
      }
      if ((value.grain || 'week') === 'week' && bucket.grain === 'week') {
        return bucket.weekKey === value.weekKey;
      }
      return false;
    });
  }, [buckets, monthBuckets, rangeOnly, value, weekBuckets]);
  const hasBuckets = selectableBuckets.length > 0;
  const visibleRangeOptions = useMemo(() => {
    const baseOptions = rangeOptions;
    if (value === 'all' || value.grain !== 'range') return baseOptions;
    if (baseOptions.some(option => weekSelectionsEqual(option, value))) return baseOptions;
    return [value, ...baseOptions];
  }, [rangeOptions, value]);
  const isInteractive = !loading && (hasBuckets || visibleRangeOptions.length > 0);
  const periodLabel = effectiveGranularity === 'month' ? 'months' : 'weeks';
  const placeholderLabel = loading ? `Loading ${periodLabel}…` : 'No data available';
  const isToolbar = variant === 'toolbar';

  const activeRange = useMemo(
    () => (value !== 'all' && value.grain === 'range' ? value : visibleRangeOptions[0] || null),
    [value, visibleRangeOptions]
  );
  const rangePresets = useMemo(
    () =>
      rangeOptions
        .filter(option => option.rangeKey !== 'custom')
        .map(option => ({
          key: option.rangeKey,
          label: option.label,
          days: Math.max(
            1,
            Math.round(
              (new Date(option.endDate).getTime() - new Date(option.startDate).getTime()) /
                (24 * 60 * 60 * 1000)
            )
          ),
        })),
    [rangeOptions]
  );
  const handleRangeChange = useCallback(
    (nextRange: AnalyticsDateRangeValue) => {
      const preset = rangeOptions.find(option => option.rangeKey === nextRange.key);
      onChange(preset || customRangeSelection(nextRange.startDate, nextRange.endDate));
    },
    [onChange, rangeOptions]
  );

  const jobById = useMemo(() => {
    const m = new Map<string, Job>();
    for (const j of jobs) m.set(j.id, j);
    return m;
  }, [jobs]);

  const mobilePeriodOptions = useMemo(() => {
    if (!isInteractive) {
      return [
        {
          value: 'placeholder',
          label: placeholderLabel,
          fullLabel: placeholderLabel,
          runCount: 0,
          isRange: false,
        },
      ];
    }

    const bucketOptions = selectableBuckets.map(bucket => ({
      value: getBucketPeriodKey(bucket),
      label: getBucketLabel(bucket),
      fullLabel: bucket.fullLabel,
      runCount: bucket.jobIds.length,
      isRange: false,
    }));
    const options =
      value !== 'all' && value.grain === 'range'
        ? [
            {
              value: 'period-placeholder',
              label: `Choose ${effectiveGranularity === 'month' ? 'month' : 'week'}`,
              fullLabel: `Switch from the selected date range to a calendar ${effectiveGranularity}`,
              runCount: 0,
              isRange: false,
            },
            ...bucketOptions,
          ]
        : bucketOptions;
    const showAllOption = showAll || value === 'all';
    if (!showAllOption) return options;
    return [
      {
        value: 'all',
        label: allLabel,
        fullLabel: `View all sampler ${effectiveGranularity === 'month' ? 'months' : 'weeks'} combined`,
        runCount: jobs.length,
        isRange: false,
      },
      ...options,
    ];
  }, [
    allLabel,
    effectiveGranularity,
    isInteractive,
    jobs.length,
    placeholderLabel,
    selectableBuckets,
    showAll,
    value,
  ]);

  const mobilePeriodCollection = useMemo(
    () => createListCollection({ items: mobilePeriodOptions }),
    [mobilePeriodOptions]
  );

  const selectedPeriodValue = isInteractive
    ? value === 'all'
      ? 'all'
      : value.grain === 'range'
        ? 'period-placeholder'
        : getSelectionPeriodKey(value)
    : 'placeholder';
  const selectedPeriodLabel =
    mobilePeriodOptions.find(option => option.value === selectedPeriodValue)?.label ??
    mobilePeriodOptions[0]?.label ??
    allLabel;

  const handleMobilePeriodChange = useCallback(
    (nextValue: string) => {
      if (!isInteractive || !nextValue) return;
      if (nextValue === 'all') {
        onChange('all');
        return;
      }
      const bucket = selectableBuckets.find(
        candidate => getBucketPeriodKey(candidate) === nextValue
      );
      if (bucket) onChange(selectionFromBucket(bucket));
    },
    [isInteractive, onChange, selectableBuckets]
  );

  const voiceRef = useVoicePageControl({
    label: 'Collection period',
    kind: 'select',
    value: selectedPeriodValue,
    options: mobilePeriodOptions.filter(option => !option.value.includes('placeholder')),
    disabled: !isInteractive || rangeOnly,
    apply: requested => {
      const next = voiceSelectOption(
        mobilePeriodOptions.filter(option => !option.value.includes('placeholder')),
        requested
      );
      handleMobilePeriodChange(next);
      return next;
    },
  });

  useEffect(() => {
    if (!scrollRef.current || value === 'all' || value.grain === 'range') return;

    const selectedIndex = selectableBuckets.findIndex(bucket => {
      if (value.grain === 'month' && bucket.grain === 'month') {
        return value.monthKey === bucket.monthKey;
      }
      if ((value.grain || 'week') === 'week' && bucket.grain === 'week') {
        return value.weekKey === bucket.weekKey;
      }
      return false;
    });
    if (selectedIndex === -1) return;

    const t = window.setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;

      const items = container.querySelectorAll('[data-week-item]');
      const selectedItem = items[selectedIndex] as HTMLElement;
      selectedItem?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 100);

    return () => window.clearTimeout(t);
  }, [value, selectableBuckets]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  return (
    <>
      <HStack
        ref={voiceRef}
        display={isToolbar || rangeOnly ? 'inline-flex' : { base: 'inline-flex', md: 'none' }}
        gap={1}
        minW={0}
        maxW="full"
        alignSelf="flex-start"
      >
        {activeRange ? (
          <AnalyticsDateRangePicker
            value={{
              key: activeRange.rangeKey,
              label: activeRange.label,
              startDate: localCalendarDateKey(activeRange.startDate),
              endDate: localCalendarDateKey(activeRange.endDate),
            }}
            presets={rangePresets}
            onChange={handleRangeChange}
            showComparison={showComparison}
            compact
            isActive={value !== 'all' && value.grain === 'range'}
            disabled={loading || rangePresets.length === 0}
          />
        ) : null}
        {!rangeOnly ? (
          <Select.Root
            collection={mobilePeriodCollection}
            value={[selectedPeriodValue]}
            onValueChange={event => handleMobilePeriodChange(event.value[0] || '')}
            size="xs"
            positioning={{ sameWidth: false, placement: 'bottom-start' }}
            disabled={!isInteractive}
          >
            <Select.HiddenSelect />
            <Select.Trigger asChild>
              <Box
                as="button"
                layerStyle="filterPill"
                {...(isToolbar ? FILTER_CONTROL_STYLE : {})}
                h={isToolbar ? '36px' : '38px'}
                minW={isToolbar ? '118px' : '126px'}
                maxW="min(58vw, 190px)"
                px={2.5}
                display="inline-flex"
                alignItems="center"
                gap="1.5"
                overflow="hidden"
                borderRadius={isToolbar ? '10px' : undefined}
                borderColor={value === 'all' ? 'ui.border.default' : 'ui.border.subtle'}
                bg={value === 'all' ? 'ui.surface.input' : 'transparent'}
                color="ui.text.primary"
                fontSize={isToolbar ? '0.8125rem' : '0.9375rem'}
                fontWeight={isToolbar ? '500' : '600'}
                cursor={isInteractive ? 'pointer' : 'default'}
                opacity={isInteractive ? 1 : 0.64}
                _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.default' }}
                _focus={{ outline: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                transition="background-color 0.12s, border-color 0.12s, color 0.12s"
                whiteSpace="nowrap"
                aria-label={`Select sampler ${effectiveGranularity === 'month' ? 'month' : 'week'}`}
                aria-disabled={!isInteractive}
              >
                <Calendar size={12} />
                <Text as="span" truncate minW={0}>
                  {selectedPeriodLabel}
                </Text>
                <Box as="span" display="inline-flex" opacity={0.45} flexShrink={0}>
                  <ChevronDown size={11} />
                </Box>
              </Box>
            </Select.Trigger>
            <Portal>
              <Select.Positioner>
                <Select.Content
                  minW="220px"
                  maxW="calc(100vw - 32px)"
                  {...PERIOD_SELECT_CONTENT_PROPS}
                  outline="none"
                >
                  {mobilePeriodCollection.items.map(item => (
                    <Select.Item key={item.value} item={item} {...PERIOD_SELECT_ITEM_PROPS}>
                      <Box minW={0}>
                        <Text fontSize="xs" color="inherit" fontWeight="600">
                          {item.label}
                        </Text>
                        <Text fontSize="2xs" color="ui.text.tertiary" lineClamp={1}>
                          {item.isRange
                            ? item.fullLabel
                            : item.value === 'all'
                              ? item.fullLabel
                              : `${item.runCount} sampler run${item.runCount === 1 ? '' : 's'}`}
                        </Text>
                      </Box>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
        ) : null}
      </HStack>

      <Box
        ref={voiceRef}
        layerStyle={isToolbar ? undefined : 'surfaceCard'}
        w="full"
        minW={0}
        h={isToolbar ? 'auto' : '52px'}
        px={isToolbar ? 0 : 3}
        display={isToolbar || rangeOnly ? 'none' : { base: 'none', md: 'flex' }}
        alignItems="center"
        flex={isToolbar ? '1 1 420px' : undefined}
      >
        <HStack gap={1} w="full" minW={0}>
          {activeRange ? (
            <AnalyticsDateRangePicker
              value={{
                key: activeRange.rangeKey,
                label: activeRange.label,
                startDate: localCalendarDateKey(activeRange.startDate),
                endDate: localCalendarDateKey(activeRange.endDate),
              }}
              presets={rangePresets}
              onChange={handleRangeChange}
              showComparison={showComparison}
              compact
              isActive={value !== 'all' && value.grain === 'range'}
              disabled={loading || rangePresets.length === 0}
            />
          ) : null}
          <IconButton
            aria-label={`Scroll ${effectiveGranularity === 'month' ? 'months' : 'weeks'} left`}
            variant="ghost"
            size="sm"
            minW="28px"
            h="28px"
            onClick={() => {
              if (isInteractive) scrollBy('left');
            }}
            color="ui.text.secondary"
            borderRadius="glassInput"
            _hover={{ bg: 'ui.surface.input' }}
            flexShrink={0}
            disabled={!isInteractive}
          >
            <ChevronLeft size={16} />
          </IconButton>

          <HStack
            ref={scrollRef}
            gap={1}
            flex={1}
            minW={0}
            overflowX="auto"
            css={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
            py={0.5}
            role="group"
            aria-label={`Sampler ${effectiveGranularity === 'month' ? 'months' : 'weeks'}`}
          >
            {(showAll || value === 'all') && isInteractive && (
              <Tooltip.Root openDelay={300}>
                <Tooltip.Trigger asChild>
                  <Box
                    as="button"
                    px={3}
                    py={1.5}
                    borderWidth="1px"
                    borderStyle="solid"
                    borderRadius="glassButton"
                    fontSize="0.9375rem"
                    fontWeight={value === 'all' ? '600' : '500'}
                    transition={PILL_COLOR_TRANSITION}
                    {...getSelectablePillProps(value === 'all')}
                    whiteSpace="nowrap"
                    cursor="pointer"
                    aria-pressed={value === 'all'}
                    aria-label={`${allLabel}. View all sampler weeks combined.`}
                    onClick={() => onChange('all')}
                  >
                    {allLabel}
                  </Box>
                </Tooltip.Trigger>
                <Portal>
                  <Tooltip.Positioner>
                    <Tooltip.Content>View all weeks combined</Tooltip.Content>
                  </Tooltip.Positioner>
                </Portal>
              </Tooltip.Root>
            )}

            {!isInteractive ? (
              <Box
                px={3}
                py={1.5}
                borderWidth="1px"
                borderStyle="solid"
                borderRadius="glassButton"
                fontSize="0.9375rem"
                fontWeight="500"
                color="ui.text.secondary"
                bg="ui.surface.input"
                borderColor="ui.border.subtle"
                whiteSpace="nowrap"
                opacity={0.64}
                aria-disabled="true"
              >
                <HStack gap={1.5}>
                  <Calendar size={14} />
                  <Text color="inherit">{placeholderLabel}</Text>
                </HStack>
              </Box>
            ) : null}

            {isInteractive &&
              selectableBuckets.map((bucket, index) => {
                const selection = selectionFromBucket(bucket);
                const isSelected = weekSelectionsEqual(value, selection);
                const isLatest = index === 0;
                const runCount = bucket.jobIds.length;
                const rangeLabel = getBucketLabel(bucket);

                const detailLines =
                  runCount > 1
                    ? bucket.jobIds
                        .map(id => {
                          const job = jobById.get(id);
                          const created = job?.created_at
                            ? format(new Date(job.created_at), 'MMM d, yyyy · h:mm a')
                            : id;
                          return created;
                        })
                        .slice(0, 5)
                    : [];

                const tooltipBody =
                  runCount > 1
                    ? [
                        bucket.fullLabel,
                        `${runCount} runs in this calendar ${bucket.grain}`,
                        detailLines.length ? detailLines.join('\n') : undefined,
                      ]
                        .filter(Boolean)
                        .join('\n')
                    : bucket.fullLabel;

                return (
                  <Tooltip.Root key={getBucketPeriodKey(bucket)} openDelay={300}>
                    <Tooltip.Trigger asChild>
                      <Box
                        as="button"
                        data-week-item
                        px={3}
                        py={1.5}
                        borderWidth="1px"
                        borderStyle="solid"
                        borderRadius="glassButton"
                        fontSize="0.9375rem"
                        fontWeight={isSelected ? '600' : '500'}
                        transition={PILL_COLOR_TRANSITION}
                        position="relative"
                        {...getSelectablePillProps(isSelected)}
                        whiteSpace="nowrap"
                        cursor="pointer"
                        aria-pressed={isSelected}
                        aria-label={
                          runCount > 1
                            ? `${rangeLabel}, ${runCount} sampler runs`
                            : `${rangeLabel}, one sampler run`
                        }
                        onClick={() => onChange(selection)}
                      >
                        <HStack gap={1.5}>
                          <Text color="inherit">{rangeLabel}</Text>
                          {isLatest && !isSelected && (
                            <Box
                              w="6px"
                              h="6px"
                              borderRadius="full"
                              bg="luminous.green"
                              aria-hidden
                            />
                          )}
                        </HStack>
                      </Box>
                    </Tooltip.Trigger>
                    <Portal>
                      <Tooltip.Positioner>
                        <Tooltip.Content whiteSpace="pre-line">{tooltipBody}</Tooltip.Content>
                      </Tooltip.Positioner>
                    </Portal>
                  </Tooltip.Root>
                );
              })}
          </HStack>

          <IconButton
            aria-label={`Scroll ${effectiveGranularity === 'month' ? 'months' : 'weeks'} right`}
            variant="ghost"
            size="sm"
            minW="28px"
            h="28px"
            onClick={() => {
              if (isInteractive) scrollBy('right');
            }}
            color="ui.text.secondary"
            borderRadius="glassInput"
            _hover={{ bg: 'ui.surface.input' }}
            flexShrink={0}
            disabled={!isInteractive}
          >
            <ChevronRight size={16} />
          </IconButton>
        </HStack>
      </Box>
    </>
  );
}
